import { Box, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { Fragment, type ReactElement } from 'react';

import { hasValue } from '@core/utils/equality';

import type { Column } from '../TableDataModel';
import { RowActions } from './RowActions';
import { RowDetails } from './RowDetails';
import { SlabTableRowCells } from './SlabTableRowCells';

type DataTableRowProps<TRow extends { id: string }> = {
  row: TRow;
  index: number;
  isSelected: boolean;
  isExpanded: boolean;
  getRowHighlight?: (row: TRow) => string | undefined;
  includeSelect: boolean;
  onSelectClick: (id: string) => void;
  onEditPress?: (value: TRow) => void;
  canEditRow: (value: TRow) => boolean;
  onArchivePress?: (row: TRow) => void;
  isRowArchived?: (row: TRow) => boolean;
  /** When false, row archive/restore is always hidden. @default true */
  canArchive?: boolean;
  renderStickyActions?: (row: TRow) => ReactElement;
  renderedColumns: ReadonlySet<Column<TRow>>;
  onIsExpandedChange: (id: string, newIsExpanded: boolean) => void;
  renderRowDetails?: (value: TRow) => ReactElement;
  stickyRowDetails?: boolean;
  nested?: boolean;
  highlightRowDetails?: boolean;
};

export const DataTableRow = <TRow extends { id: string }>({
  row,
  index,
  isSelected,
  isExpanded,
  getRowHighlight,
  includeSelect,
  onSelectClick,
  onEditPress,
  canEditRow,
  onArchivePress,
  isRowArchived,
  canArchive = true,
  renderStickyActions,
  renderedColumns,
  onIsExpandedChange,
  renderRowDetails,
  stickyRowDetails,
  nested,
  highlightRowDetails,
}: DataTableRowProps<TRow>): ReactElement => {
  const theme = useTheme();
  const showRowArchive = hasValue(onArchivePress) && canArchive;
  const hasStickyActions = hasValue(onEditPress) || showRowArchive || hasValue(renderStickyActions);
  const selectedColor = theme.palette.SlabBlue.Selected;
  const labelID = `enhanced-table-checkbox-${index}`;
  const rowHighlight =
    isExpanded && highlightRowDetails ? theme.palette.SlabGray[100] : getRowHighlight?.(row);
  const restingColor = isSelected ? selectedColor : (rowHighlight ?? 'white');
  const hoverBackground = `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover}), ${restingColor}`;
  const stickyCellSx = {
    position: 'sticky',
    zIndex: 1,
    backgroundColor: restingColor,
    'tr.MuiTableRow-hover:hover &': { background: hoverBackground },
  } as const;
  let rowDetailsBackground: undefined | string;
  if (isSelected) {
    rowDetailsBackground = selectedColor;
  } else if (hasValue(rowHighlight)) {
    rowDetailsBackground = rowHighlight;
  }

  return (
    <>
      <TableRow
        hover={!isExpanded && !nested}
        role='checkbox'
        aria-checked={isSelected}
        tabIndex={-1}
        selected={isSelected}
        sx={{
          backgroundColor: rowHighlight,
          '&.MuiTableRow-hover:hover': { background: hoverBackground },
        }}
      >
        {includeSelect && (
          <Fragment key={`table-row-checkbox-select-${row.id}`}>
            <TableCell sx={{ ...stickyCellSx, left: 0 }} padding='checkbox'>
              <Checkbox
                checked={isSelected}
                inputProps={{
                  'aria-labelledby': labelID,
                }}
                onClick={(): void => onSelectClick(row.id)}
              />
            </TableCell>
          </Fragment>
        )}
        <SlabTableRowCells
          renderedColumns={renderedColumns}
          row={row}
          labelID={labelID}
          isExpandable={hasValue(renderRowDetails)}
          isExpanded={isExpanded}
          onIsExpandedChange={onIsExpandedChange}
        />
        {hasStickyActions && (
          <Fragment key={`table-row-edit-button-${row.id}`}>
            <TableCell padding='checkbox' sx={{ ...stickyCellSx, right: 0 }}>
              <Box marginX={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RowActions
                  row={row}
                  onEditPress={canEditRow(row) ? onEditPress : undefined}
                  onArchivePress={onArchivePress}
                  isRowArchived={isRowArchived}
                  canArchive={canArchive}
                />
                {renderStickyActions?.(row)}
              </Box>
            </TableCell>
          </Fragment>
        )}
      </TableRow>
      {isExpanded && hasValue(renderRowDetails) && (
        <RowDetails
          renderChildren={() => renderRowDetails(row)}
          colSpan={renderedColumns.size}
          backgroundColor={rowDetailsBackground}
          sticky={stickyRowDetails}
        />
      )}
    </>
  );
};
